import { useQuery } from "@apollo/client";
import { useCallback } from "react";
import { DATA_MANAGEMENT_FILES_QUERY } from "@/api/queries/dataManagementFiles";
import {
  DataManagementFileOrderingInput,
  SortDirectionEnum,
  DataManagementFileSortableFieldEnum,
} from "@/__generated__/types";

export interface DataManagementFile {
  id: string;
  name: string;
  url: string;
  createdAt: number;
}

export interface UseDataManagementFilesConfig {
  orgId?: string;
  first?: number;
  after?: string;
  before?: string;
  ordering?: DataManagementFileOrderingInput[];
}

export interface UseDataManagementFilesReturn {
  data: DataManagementFile[];
  loading: boolean;
  error: Error | null;
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor?: string;
  endCursor?: string;
  refetch: () => void;
}

export const useDataManagementFiles = (
  config: UseDataManagementFilesConfig = {},
): UseDataManagementFilesReturn => {
  const {
    orgId,
    first = 10,
    after,
    before,
    ordering = [
      {
        field: DataManagementFileSortableFieldEnum.CreatedAt,
        direction: SortDirectionEnum.Desc,
      },
    ],
  } = config;

  const { data, loading, error, refetch } = useQuery(
    DATA_MANAGEMENT_FILES_QUERY,
    {
      variables: {
        orgId: orgId || "",
        first,
        after,
        before,
        ordering,
      },
      skip: !orgId,
    },
  );

  const files =
    data?.node?.__typename === "Organization"
      ? data.node.dataManagementFiles?.edges
          ?.map((edge) => edge?.node)
          .filter(
            (node): node is DataManagementFile =>
              node !== null && node !== undefined,
          ) || []
      : [];

  const pageInfo =
    data?.node?.__typename === "Organization"
      ? data.node.dataManagementFiles?.pageInfo
      : null;

  const refetchData = useCallback(() => {
    if (orgId) {
      refetch();
    }
  }, [orgId, refetch]);

  return {
    data: files,
    loading,
    error: error || null,
    totalCount:
      data?.node?.__typename === "Organization"
        ? data.node.dataManagementFiles?.totalCount || 0
        : 0,
    hasNextPage: pageInfo?.hasNextPage || false,
    hasPreviousPage: pageInfo?.hasPreviousPage || false,
    startCursor: pageInfo?.startCursor || undefined,
    endCursor: pageInfo?.endCursor || undefined,
    refetch: refetchData,
  };
};
